import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { usePagination } from '@/hooks/usePagination';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { BacklogItem, ProjectSprint } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsSprintsPage() {
  const pagination = usePagination();
  const { projects } = useProjectList();
  const [data, setData] = useState<ProjectSprint[]>([]);
  const [backlog, setBacklog] = useState<BacklogItem[]>([]);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ projectId: 0, sprintCode: '', sprintName: '', goal: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.listSprints({ page: pagination.page, size: pagination.size });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load sprints');
    } finally {
      setLoading(false);
    }
  }, [pagination]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!projectId) { setBacklog([]); return; }
    projectsApi.listBacklog(projectId).then(setBacklog).catch((e) => showApiError(e, 'Backlog load failed'));
  }, [projectId]);

  const columns: Column<ProjectSprint>[] = [
    { key: 'sprintCode', header: 'Code' },
    { key: 'sprintName', header: 'Name' },
    { key: 'statusCode', header: 'Status' },
    { key: 'projectId', header: 'Project' },
    { key: 'velocityPoints', header: 'Velocity' },
    {
      key: 'actions',
      header: 'Actions',
      actionsWrap: true,
      render: (row) => row.statusCode === 'ACTIVE' ? (
        <TableRowActions layout="wrap">
          <Button variant="secondary" size="sm" onClick={() => void completeSprint(row.id)}>Complete</Button>
          {backlog.slice(0, 3).map((b) => (
            <Button key={b.id} variant="ghost" size="sm"
              onClick={() => void assignToSprint(row.id, b.taskId)}>+ {b.taskTitle ?? b.taskId}</Button>
          ))}
        </TableRowActions>
      ) : null,
    },
  ];

  const start = async () => {
    try {
      await projectsApi.startSprint(form);
      notify.success('Sprint started');
      setOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Start sprint failed');
    }
  };

  const completeSprint = async (sprintId: number) => {
    try {
      await projectsApi.completeSprint(sprintId);
      notify.success('Sprint completed');
      load();
    } catch (err) {
      showApiError(err, 'Complete sprint failed');
    }
  };

  const assignToSprint = async (sprintId: number, taskId: number) => {
    try {
      await projectsApi.assignSprintTask({ sprintId, taskId });
      notify.success('Task added to sprint');
      if (projectId) projectsApi.listBacklog(projectId).then(setBacklog);
      load();
    } catch (err) {
      showApiError(err, 'Assign failed');
    }
  };

  return (
    <div>
      <PageHeader title="Sprints" description="Backlog, sprint planning, execution, and velocity"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Sprints' }]}
        actions={<Button onClick={() => setOpen(true)}><AddIcon fontSize="small" /> Start sprint</Button>} />
      <div className="mb-4 flex gap-3 flex-wrap items-start">
        <Select
          placeholder="Backlog project"
          value={String(projectId ?? '')}
          onChange={(v) => setProjectId(v ? Number(v) : undefined)}
          options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
        />
        {projectId && (
          <div className="card p-3 flex-1 min-w-[280px]">
            <h3 className="text-sm font-semibold mb-2">Product backlog ({backlog.length})</h3>
            <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
              {backlog.map((b) => <li key={b.id}>{b.taskTitle ?? `Task #${b.taskId}`}</li>)}
            </ul>
          </div>
        )}
      </div>
      <DataTable columns={columns} data={data} keyField="id" loading={loading}
        page={pagination.page} totalPages={pagination.totalPages} totalElements={pagination.totalElements} onPageChange={pagination.setPage} />
      <Modal open={open} onClose={() => setOpen(false)} title="Start sprint" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void start()}>Start</Button></>}>
        <div className="space-y-3">
          <Select
            placeholder="Select project"
            value={String(form.projectId || '')}
            onChange={(v) => setForm({ ...form, projectId: v ? Number(v) : 0 })}
            options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
          />
          <Input label="Sprint code" value={form.sprintCode} onChange={(e) => setForm({ ...form, sprintCode: e.target.value })} />
          <Input label="Sprint name" value={form.sprintName} onChange={(e) => setForm({ ...form, sprintName: e.target.value })} />
          <Input label="Goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
